import { BadgeProps } from '@mui/material';
import { Delete, MoreHoriz } from '@mui/icons-material';
import StatusBadge from 'components/new/StatusBadge';
import { getQueryKey } from '@trpc/react-query';
import PageFilters, { Filter } from 'components/new/PageFilters';
import { BankAccountOperationStatus } from 'further-types/lp/bank-reconciliation';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { Column, Row } from 'further-ui/components/Layout';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import TooltipTypography from 'further-ui/components/TooltipTypography';
import SearchField from 'further-ui/components/SearchField';
import { useDisclosure, useFilters, usePagination } from 'further-ui/hooks';
import {
  BankAccountOperationSourceLabels,
  BankAccountOperationStatusLabels,
} from 'further-ui/labels';
import {
  CalendarDay,
  DateRange,
  numberToCurrencyString,
} from 'further-ui/utils';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { MuiDateRangePicker } from 'components/FormElements/DateRangePicker';
import api from 'lib/trpcClient';
import { useState } from 'react';
import Menu from 'further-ui/components/Menu';
import { SortOrder } from 'further-types/utils';

type Props = {
  bankAccount: {
    id: string;
    vehicleId: string;
  };
};

const statusColorMap: Record<BankAccountOperationStatus, BadgeProps['color']> =
  {
    [BankAccountOperationStatus.Reconciled]: 'success',
    [BankAccountOperationStatus.Unreconciled]: 'error',
  };

const BankStatement: React.FC<Props> = ({ bankAccount }) => {
  const pageId = `bank-statement-${bankAccount.id}`;
  const deleteDisclosure = useDisclosure();
  const [selectedOperations, setSelectedOperations] = useState<Array<string>>(
    [],
  );
  const pagination = usePagination({
    id: pageId,
    orderBy: 'date',
    order: SortOrder.Desc,
  });

  const { filters, handleChangeFilters } = useFilters<{
    search: string;
    dateRange?: DateRange;
  }>(pageId, {
    defaultFilters: { search: '', dateRange: undefined },
    onFiltersChange: pagination.toFirstPage,
  });

  const [data] = api.bankAccount.listBankAccountOperations.useSuspenseQuery({
    vehicleId: bankAccount.vehicleId,
    bankAccountId: bankAccount.id,
    page: pagination.page,
    rowsPerPage: pagination.rowsPerPage,
    orderBy: pagination.orderBy,
    order: pagination.order,
    search: filters.search || undefined,
    dateFrom: filters.dateRange?.startDate
      ? new CalendarDay(filters.dateRange.startDate)
      : undefined,
    dateTo: filters.dateRange?.endDate
      ? new CalendarDay(filters.dateRange.endDate)
      : undefined,
  });

  const [canDeleteData] =
    api.bankAccount.canDeleteBankAccountOperations.useSuspenseQuery({
      vehicleId: bankAccount.vehicleId,
      bankAccountId: bankAccount.id,
    });

  const deleteOperations =
    api.bankAccount.deleteBankAccountOperations.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Operations deleted successfully.',
        invalidations: [getQueryKey(api.bankAccount.listBankAccountOperations)],
      }),
    );

  const handleDelete = () => {
    if (deleteOperations.isPending || !selectedOperations.length) return;

    deleteDisclosure.onClose();
    deleteOperations.mutate({
      bankAccountId: bankAccount.id,
      operationIds: selectedOperations,
      vehicleId: bankAccount.vehicleId,
    });
  };

  const canDelete = canDeleteData?.canDelete ?? true;
  const deleteDisabledReason = canDeleteData?.reason;

  type Row = (typeof data)['results'][number];

  const columns: Array<TableColumn<Row>> = [
    {
      key: 'date',
      label: 'Date',
      width: 100,
      render: (row) => row.date.toReadable(),
    },
    {
      key: 'counterparty',
      label: 'Counterparty',
      width: 160,
    },
    {
      key: 'description',
      label: 'Details',
      width: 200,
      render: (row) => (
        <TooltipTypography
          color="grey.600"
          noWrap
          variant="inherit"
          tooltip={row.description}
        >
          {row.description}
        </TooltipTypography>
      ),
    },
    {
      key: 'spent',
      label: 'Sum',
      width: 150,
      render: (row) =>
        numberToCurrencyString((row.received ?? 0) - (row.spent ?? 0), {
          currency: row.accountCurrency,
          negativeInBrackets: true,
        }),
    },
    {
      key: 'runningBalance',
      label: 'Running balance',
      width: 180,
      render: (row) =>
        numberToCurrencyString(row.runningBalance, {
          currency: row.accountCurrency,
          negativeInBrackets: true,
        }),
    },
    {
      key: 'source',
      label: 'Source',
      width: 120,
      render: (row) => BankAccountOperationSourceLabels[row.source],
    },
    {
      key: 'status',
      label: 'Status',
      width: 70,
      render: (row) => (
        <StatusBadge
          color={statusColorMap[row.status]}
          tooltip={BankAccountOperationStatusLabels[row.status]}
        />
      ),
    },
  ];

  const rows = data?.results ?? [];

  return (
    <>
      <Column spacing="lg">
        <PageFilters justify="space-between">
          <Filter size="md">
            <SearchField
              autoSearchOnDebounce
              name="search"
              placeholder="Search by description or counterparty name"
              fullWidth
              value={filters.search}
              onSearch={(search) => handleChangeFilters({ search })}
              allowEmptyQuery
            />
          </Filter>
          <Filter size="md">
            <Row fullWidth centered spacing="sm">
              <MuiDateRangePicker
                dateRange={filters.dateRange}
                onDateRangeChange={(dateRange) =>
                  handleChangeFilters({ dateRange })
                }
                placeholder="Select date range"
              />
              <Menu
                handleVariant="square"
                handleSize="medium"
                handleIcon={MoreHoriz}
                alignWithIconRightEdge
                items={[
                  {
                    label: 'Delete selected statement lines',
                    icon: Delete,
                    disabled: !selectedOperations.length || !canDelete,
                    tooltip:
                      !canDelete && deleteDisabledReason
                        ? deleteDisabledReason
                        : undefined,
                    onClick: deleteDisclosure.onOpen,
                  },
                ]}
              />
            </Row>
          </Filter>
        </PageFilters>

        <Table
          scrollable
          disableSorting
          columns={columns}
          rows={rows}
          emptyMessage="No statement lines found"
          pagination={pagination}
          rowsTotalCount={data?.totalCount ?? 0}
          checkboxes={{
            isCheckedRow: (row) => selectedOperations.includes(row.id),
            onRowCheckedToggle: (row) =>
              setSelectedOperations((prev) =>
                prev.includes(row.id)
                  ? prev.filter((id) => id !== row.id)
                  : [...prev, row.id],
              ),
            allRowsChecked: rows.every((row) =>
              selectedOperations.includes(row.id),
            ),
            onCheckAllToggle: (checked) =>
              setSelectedOperations(checked ? rows.map((row) => row.id) : []),
          }}
        />
      </Column>
      <ConfirmDialog
        open={deleteDisclosure.isOpen}
        onClose={deleteDisclosure.onClose}
        title="Are you sure?"
        content="This will change your overall statement balance. This is generally only required to remove duplicate statement lines."
        actions={{
          confirm: {
            onClick: handleDelete,
            label: 'Delete',
            color: 'error',
          },
        }}
      />
    </>
  );
};

export default BankStatement;
